import { useState, FormEvent, useRef } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

/* Número no formato internacional (sem + nem espaços) */
const WHATSAPP_NUMBER = '5521974393072';

/* Opções de área jurídica */
const AREAS_OPTIONS = [
  'Direito Trabalhista',
  'Direito Civil',
  'Direito Previdenciário',
  'Direito de Família',
  'Outro',
];

export default function Contact() {
  const { ref, isVisible } = useScrollAnimation<HTMLElement>();
  const [submitted, setSubmitted] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  /**
   * Ao submeter:
   * 1. Impede comportamento padrão do formulário
   * 2. Lê os valores dos campos
   * 3. Monta a mensagem no formato exato solicitado
   * 4. Codifica com encodeURIComponent
   * 5. Abre o WhatsApp em nova aba com a mensagem pronta
   */
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    // 1. Previne recarregamento / envio padrão
    e.preventDefault();

    const form = e.currentTarget;
    const data = new FormData(form);

    // 2. Captura valores dos campos
    const nome    = (data.get('name')    as string).trim();
    const email   = (data.get('email')   as string).trim();
    const area    = (data.get('area')    as string).trim();
    const message = (data.get('message') as string).trim();

    // 3. Monta a mensagem no formato exato
    const texto =
      `Nome Completo: ${nome} | ` +
      `E-mail: ${email} | ` +
      `Área do Caso: ${area} | ` +
      `Motivo do Contato: ${message}`;

    // 4. Codifica para URL
    const encoded = encodeURIComponent(texto);

    // 5. Abre o WhatsApp em nova aba com a mensagem pronta
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`, '_blank');

    // Exibe tela de confirmação e reseta o formulário
    setSubmitted(true);
    form.reset();
    setCharCount(0);
  };

  return (
    <section id="contato" className="py-24 lg:py-32 bg-bege" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Cabeçalho da seção */}
        <div className={`text-center mb-16 reveal ${isVisible ? 'visible' : ''}`}>
          <span className="font-sans text-dourado-medio text-xs tracking-widest uppercase">
            Entre em Contato
          </span>
          <h2 className="font-serif text-marrom-escuro text-4xl lg:text-5xl font-light mt-3 mb-4">
            Fale <span className="font-semibold">Conosco</span>
          </h2>
          <div className="w-14 h-0.5 bg-dourado-principal mx-auto mb-4" />
          <p className="font-sans text-marrom-medio text-base max-w-xl mx-auto">
            Estamos prontos para ouvir sua situação e oferecer a orientação jurídica que você precisa.
          </p>
        </div>

        <div className="flex flex-col gap-10">

          {/* Formulário de Contato */}
          <div className={`w-full reveal-left ${isVisible ? 'visible' : ''}`}>
            <div className="bg-white p-8 rounded-lg border border-marrom-claro/20">
              <h3 className="font-serif text-marrom-escuro text-2xl font-semibold mb-6">
                Envie sua mensagem
              </h3>

              {submitted ? (
                /* Tela de confirmação após envio */
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center bg-dourado-principal/15 rounded-full mx-auto mb-4">
                    <i className="ri-whatsapp-line text-dourado-principal text-3xl" />
                  </div>
                  <h4 className="font-serif text-marrom-escuro text-2xl font-semibold mb-2">
                    WhatsApp aberto!
                  </h4>
                  <p className="font-sans text-marrom-medio text-sm max-w-sm mx-auto">
                    Sua mensagem foi preparada e o WhatsApp abriu em uma nova aba. Basta clicar em enviar por lá.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-6 font-sans text-sm text-dourado-principal hover:text-dourado-medio underline cursor-pointer"
                  >
                    Preencher novamente
                  </button>
                </div>
              ) : (
                /* Formulário */
                <form
                  ref={formRef}
                  onSubmit={handleSubmit}
                  id="contact-form-whatsapp"
                  className="space-y-4"
                >

                  {/* Nome + E-mail lado a lado */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="font-sans text-marrom-escuro text-xs font-bold tracking-wide uppercase block mb-1.5">
                        Nome Completo *
                      </label>
                      <input
                        name="name"
                        type="text"
                        required
                        placeholder="Seu nome completo"
                        className="w-full font-sans text-sm border border-marrom-claro/40 rounded-md px-4 py-3 focus:outline-none focus:border-dourado-principal bg-bege/50 text-grafite placeholder-marrom-claro/60 transition-colors duration-200"
                      />
                    </div>
                    <div>
                      <label className="font-sans text-marrom-escuro text-xs font-bold tracking-wide uppercase block mb-1.5">
                        E-mail *
                      </label>
                      <input
                        name="email"
                        type="email"
                        required
                        placeholder="seu@email.com"
                        className="w-full font-sans text-sm border border-marrom-claro/40 rounded-md px-4 py-3 focus:outline-none focus:border-dourado-principal bg-bege/50 text-grafite placeholder-marrom-claro/60 transition-colors duration-200"
                      />
                    </div>
                  </div>

                  {/* Área do Caso */}
                  <div>
                    <label className="font-sans text-marrom-escuro text-xs font-bold tracking-wide uppercase block mb-1.5">
                      Área do Caso *
                    </label>
                    <select
                      name="area"
                      required
                      defaultValue=""
                      className="w-full font-sans text-sm border border-marrom-claro/40 rounded-md px-4 py-3 focus:outline-none focus:border-dourado-principal bg-bege/50 text-grafite transition-colors duration-200 cursor-pointer"
                    >
                      <option value="" disabled>Selecione a área jurídica</option>
                      {AREAS_OPTIONS.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>

                  {/* Mensagem */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="font-sans text-marrom-escuro text-xs font-bold tracking-wide uppercase">
                        Mensagem *
                      </label>
                      <span className={`font-sans text-xs ${charCount > 450 ? 'text-red-500' : 'text-marrom-claro'}`}>
                        {charCount}/500
                      </span>
                    </div>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="Descreva brevemente sua situação..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full font-sans text-sm border border-marrom-claro/40 rounded-md px-4 py-3 focus:outline-none focus:border-dourado-principal bg-bege/50 text-grafite placeholder-marrom-claro/60 transition-colors duration-200 resize-none"
                    />
                  </div>

                  {/* Botão de envio — abre o WhatsApp */}
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-3 bg-marrom-escuro hover:bg-grafite text-white font-sans font-bold text-sm px-8 py-4 rounded-md transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-whatsapp-line text-dourado-principal text-xl" />
                    </div>
                    Enviar pelo WhatsApp
                  </button>

                </form>
              )}
            </div>
          </div>

          {/* Cards de contato */}
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4 reveal-right ${isVisible ? 'visible' : ''}`}>
            {[
              {
                icon: 'ri-whatsapp-line',
                title: 'WhatsApp',
                content: '+55 (21) 97439-3072',
                link: 'https://wa.me/5521974393072?text=Ol%C3%A1!%20Gostaria%20de%20uma%20consulta%20jur%C3%ADdica.',
              },
              {
                icon: 'ri-instagram-line',
                title: 'Instagram',
                content: '@carvalhofranca.adv',
                link: 'https://instagram.com/carvalhofranca.adv',
              },
            ].map((info) => (
              <div
                key={info.title}
                className="flex gap-4 p-5 bg-white rounded-lg border border-marrom-claro/20"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-marrom-escuro rounded-lg flex-shrink-0">
                  <i className={`${info.icon} text-dourado-principal text-xl`} />
                </div>
                <div>
                  <p className="font-sans text-marrom-medio text-xs font-bold tracking-wide uppercase mb-1">
                    {info.title}
                  </p>
                  <a
                    href={info.link}
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="font-serif text-marrom-escuro text-lg font-medium hover:text-dourado-principal transition-colors duration-200 cursor-pointer"
                  >
                    {info.content}
                  </a>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}
